import React from 'react';
import {
    Box,
    Checkbox,
    Radio,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
} from '@mui/material';
import FormFieldTooltip from './FormFieldTooltip';

export type CheckboxListOption<T extends string | number> = {
    value: T;
    label: React.ReactNode;
    disabled?: boolean;
    infoLink?: string;
};

export type CheckboxMultiSelectListProps<T extends string | number> = {
    label?: string;
    options: CheckboxListOption<T>[];
    value: T[];
    onChange: (next: T[]) => void;
    disabled?: boolean;
    dense?: boolean;
    maxHeight?: number | string;
    width?: number | string;
    isSingle?: boolean;
    infoLink?: string;
};

export const MultipleList = <T extends string | number>({
    label,
    options,
    value,
    onChange,
    disabled = false,
    dense = true,
    maxHeight = 350,
    width = '100%',
    isSingle = false,
    infoLink,
}: CheckboxMultiSelectListProps<T>) => {
    const valueSet = React.useMemo(() => new Set<T>(value), [ value ]);

    const toggle = (v: T) => {
        if (disabled) {
            return;
        }

        if (isSingle) {
            onChange([ v ]);
        } else {
            if (valueSet.has(v)) {
                onChange(value.filter((x) => x !== v));
            } else {
                onChange([ ...value, v ]);
            }
        }
    };

    const StartComponent = isSingle ? Radio : Checkbox;

    return (
        <Box sx={{ width }}>
            {label && (
                <div style={{
                    display: 'flex', alignItems: 'center', marginBottom: 4, fontSize: 14, fontWeight: 'bold',
                }}>
                    {label}
                    {infoLink && <FormFieldTooltip url={infoLink}/>}
                </div>
            )}

            <Paper variant="outlined" sx={{ overflow: 'auto', maxHeight, background: 'transparent' }}>
                <List dense={dense} disablePadding>
                    {options.map((o) => {
                        const checked = valueSet.has(o.value);
                        const itemDisabled = disabled || !!o.disabled;
                        return (
                            <ListItemButton
                                key={String(o.value)}
                                onClick={() => toggle(o.value)}
                                disabled={itemDisabled}
                                sx={{ px: 1.25 }}
                            >
                                <ListItemIcon sx={{ minWidth: 34 }}>
                                    <StartComponent
                                        edge="start"
                                        size="small"
                                        checked={checked}
                                        tabIndex={-1}
                                        disableRipple
                                        sx={{ py: '0' }}
                                    />

                                </ListItemIcon>
                                <ListItemText primary={
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        {o.label}
                                        {o.infoLink && <FormFieldTooltip url={o.infoLink}/>}
                                    </div>
                                }/>
                            </ListItemButton>
                        );
                    })}
                </List>
            </Paper>
        </Box>
    );
};